import { useState, useEffect } from 'react';

/**
 * Material Design 3 adaptive window size classes.
 * Maps to the breakpoints defined in ezhard-root/tokens/tokens.css.
 */
export type WindowSizeClass =
  | 'compact'
  | 'medium'
  | 'expanded'
  | 'large'
  | 'xlarge';

/** Breakpoint thresholds matching --breakpoint-* tokens. */
const BREAKPOINTS = {
  medium: 600,
  expanded: 840,
  large: 1200,
  xlarge: 1600,
} as const;

function getWindowSizeClass(width: number): WindowSizeClass {
  if (width >= BREAKPOINTS.xlarge) return 'xlarge';
  if (width >= BREAKPOINTS.large) return 'large';
  if (width >= BREAKPOINTS.expanded) return 'expanded';
  if (width >= BREAKPOINTS.medium) return 'medium';
  return 'compact';
}

/**
 * Returns the current MD3 window size class.
 * Updates reactively when the window is resized
 * using matchMedia listeners (no resize polling).
 */
export function useWindowSizeClass(): WindowSizeClass {
  const [sizeClass, setSizeClass] = useState<WindowSizeClass>(() =>
    getWindowSizeClass(window.innerWidth),
  );

  useEffect(() => {
    const queries = Object.entries(BREAKPOINTS).map(([key, px]) => ({
      key: key as keyof typeof BREAKPOINTS,
      mql: window.matchMedia(`(min-width: ${px}px)`),
    }));

    function update() {
      setSizeClass(getWindowSizeClass(window.innerWidth));
    }

    for (const { mql } of queries) {
      mql.addEventListener('change', update);
    }

    return () => {
      for (const { mql } of queries) {
        mql.removeEventListener('change', update);
      }
    };
  }, []);

  return sizeClass;
}
